import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { AdministrationService } from "../../service/administration.service";
import { QualitativeActions } from "./qualitative.reducer";
import { catchError, concatMap, map, mergeMap, of } from "rxjs";
import {
  MacroSection,
  MAP_QUESTION_GROUP,
  SaveEvent
} from "../../shared/models/qualitative.model";
import { QuestionSections } from "../../shared/components/questionnaire.component";
import { QualitativeService } from "../../service/qualitative.service";
import { AnswerGroupCategoryType, CreateQuestionRequest, MacroSectionQueryResponse } from "../../../../graphql/generated";

export type GraphResponse<T> = {
  data: {
    [id: string]: T[];
  };
};

@Injectable()
export class QualitativeEffects {
  loadQualitatives$ = createEffect(
    (qs: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.loadQualitative),
        mergeMap(({ parameters }) =>
          qs.getQualitative(parameters).pipe(
            map((qualitative) => {
              return QualitativeActions.loadQualitativeSuccess({
                qualitative: qualitative.data.macroSections
              });
            }),
            catchError((error) => {
              return of(QualitativeActions.loadQualitativeFailure({ error }));
            })
          )
        )
      );
    }
  );

  deleteQualitativeQuestions = createEffect(() => {
    return this.actions$.pipe(
      ofType(QualitativeActions.deleteQualitativeQuestions),
      mergeMap(({ event }) => {
        return event.map((e) =>
          QualitativeActions.deleteQuestion({ event: e })
        );
      })
    );
  });

  deleteQuestion = createEffect(
    (qs: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.deleteQuestion),
        mergeMap(({ event }) => qs.deleteQualitative(event["id"]))
      );
    }
  );

  deleteQuestions$ = createEffect(
    (rs: QualitativeService = inject(QualitativeService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.deleteQuestions),
        concatMap((props) =>
          rs.deleteQuestions(props.ids).pipe(
            map(() => {
              return QualitativeActions.loadQualitative({
                parameters: {
                  page: 0,
                  rows: 10000
                }
              });
            }),
            catchError((error) => of(QualitativeActions.deleteQuestionsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  // createSections = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(QualitativeActions.createSections),
  //     mergeMap(({ event }) => {
  //       return event.map((e) => QualitativeActions.createSection({ event: e }));
  //     })
  //   );
  // });

  createSections = createEffect(
    (qs: QualitativeService = inject(QualitativeService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.createSections),
        mergeMap(({ event }) =>
          qs.createSections(event).pipe(
            mergeMap((response) => {
              const questions: CreateQuestionRequest[] = [];
              event.forEach(e => {
                e.questions.forEach(q => {
                  questions.push({
                    macSecId: Number(response?.data?.find(sec => sec?.description === e.titleSection)?.id),
                    answerGroupCategoryType: q.type as AnswerGroupCategoryType,
                    content: q.title,
                    questionOrder: q.order
                  })
                })
              })
              return [
                QualitativeActions.createSectionsSuccess({ sections: response?.data as MacroSectionQueryResponse[] }),
                QualitativeActions.createQuestions({ event: questions as SaveEvent[] })
              ];
            }),
            catchError((error) => {
              console.error("Error loading qualitative data:", error);
              return of(QualitativeActions.createSectionFailure({ error }));
            })
          )
        )
      );
    }
  );



  createSection = createEffect(
    (qs: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.createSection),
        mergeMap(({ event }) =>
          qs.createSection(event.titleSection, event.titleSection).pipe(
            map((response) => {
              //aggiorno l'id della sezione per ogni domanda
              const updatedEvent = this.updateSectionId(event, response);
              return QualitativeActions.createQuestions({
                event: updatedEvent.questions as SaveEvent[]
              });
            }),
            catchError((error) => {
              console.error("Error loading qualitative data:", error);
              return of(QualitativeActions.createSectionFailure({ error }));
            })
          )
        )
      );
    }
  );

  // createQuestions = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(QualitativeActions.createQuestions),
  //     mergeMap(({ event }) => {
  //       return event.map((e) =>
  //         QualitativeActions.createQuestion({ event: e })
  //       );
  //     })
  //   );
  // });

  createQuestions$ = createEffect(
    (rs: QualitativeService = inject(QualitativeService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.createQuestions),
        concatMap((props) =>
          rs.createQuestions(props.event).pipe(
            map(() => {
              return QualitativeActions.loadQualitative({
                parameters: {
                  page: 0,
                  rows: 10000
                }
              });
            }),
            catchError((error) => of(QualitativeActions.deleteQuestionsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  createQuestion = createEffect(
    (qs: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.createQuestion),
        mergeMap(({ event }) => {
          return qs
            .createQuestion(
              Number(event.sectionId),
              event.order,
              event.title,
              event?.type ? MAP_QUESTION_GROUP[event?.type] : undefined
            )
            .pipe(
              map((event) => {
                return QualitativeActions.loadQualitative({
                  parameters: {
                    page: 0,
                    rows: 10000
                  }
                });
              }),
              catchError((error) => {
                console.error("Error loading qualitative data:", error);
                return of(QualitativeActions.createQuestionFailure({ error }));
              })
            );
        })
      );
    }
  );

  updateQuestions = createEffect(() => {
    return this.actions$.pipe(
      ofType(QualitativeActions.updateQuestions),
      mergeMap(({ event }) => {
        return event.map((e) =>
          QualitativeActions.updateQuestion({ event: e })
        );
      })
    );
  });

  updateQuestion = createEffect(
    (qs: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.updateQuestion),
        mergeMap(({ event }) => {
          const { id } = event;
          return qs
            .updateQuestion(
              id,
              this.checkEvent(event.type).toUpperCase(),
              event.title,
              String(event.sectionId),
              event.order
            )
            .pipe(
              map((event) => {
                return QualitativeActions.loadQualitative({
                  parameters: { page: 0, rows: 10000 }
                });
              }),
              catchError((error) => {
                console.error("Error loading qualitative data:", error);
                return of(QualitativeActions.updateQuestionFailure({ error }));
              })
            );
        })
      );
    }
  );

  updateSectionTitle$ = createEffect(
    (rs: QualitativeService = inject(QualitativeService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.updateSectionTitle),
        concatMap((props) =>
          rs.updateSectionTitle(props.event).pipe(
            map(() => {
              return QualitativeActions.updateSectionTitleSuccess(
                {
                  event: {}
                }
              );
            }),
            catchError((error) => of(QualitativeActions.updateSectionTitleFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  deleteSections = createEffect(
    (qs: QualitativeService = inject(QualitativeService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.deleteSections),
        mergeMap(({ sectionIds }) =>
          qs.deleteSections(sectionIds).pipe(
            map((response) => {
              return QualitativeActions.loadQualitative({
                parameters: { page: 0, rows: 10000 }
              });
            }),
            catchError((error) =>
              of(
                QualitativeActions.deleteSectionsFailure()
              )
            )
          )
        )
      );
    }
  );

  deleteSection = createEffect(
    (qs: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(QualitativeActions.deleteSection),
        mergeMap(({ event }) => qs.deleteSection(event.sectionId))
      );
    }
  );

  updateSectionId(
    event: QuestionSections & { sectionIndex: number },
    response: any
  ) {
    return {
      ...event,
      questions: event.questions.map((q) => ({
        ...q,
        sectionId: response.data.createMacroSection.data[0].id
      }))
    };
  }


  checkEvent(event: string): string {
    return event.toUpperCase() === "YESORNO" ? "YES_OR_NO" : event;
  }

  constructor(private actions$: Actions) { }
}
